import { IAnalysisResponse } from '../../utils/apollo/responseTypes';
import { IAnalysisMetadata } from '../slices/analyses';
import { parseAnalysisId } from './transientAnalysis';
import { getQueryTypePagePath } from '../../utils';
import { IBreakdownOverallStatus } from '../../utils/interfaces';
import { isEmpty, isNil } from 'lodash';
import { AnalysisStatus } from '../../utils/apollo/graphqlSchema';
import { AnalysisErrorStatus } from '../../utils/apollo/graphqlSchema';
import { ChartViewType } from '../../components/Chart/types';
import { IChartView } from '../../components/Chart/types';
import { UnreachableCaseError } from 'ts-essentials';

export const getChartView = (analysis: IAnalysisResponse) => {
  const { id, status, errorStatus, failureMsg, data } = analysis;
  const isEmptyData = data?.isEmpty || status !== AnalysisStatus.Success;

  if (
    status === AnalysisStatus.Success ||
    status === AnalysisStatus.Pending ||
    status === AnalysisStatus.Running
  ) {
    if (status === AnalysisStatus.Success && isEmptyData) {
      return {
        type: ChartViewType.NoData,
      } satisfies IChartView;
    } else {
      return { type: ChartViewType.Default } satisfies IChartView;
    }
  } else if (status === AnalysisStatus.Cancelled) {
    return {
      type: ChartViewType.Cancelled,
    } satisfies IChartView;
  } else if (status === AnalysisStatus.Failed) {
    if (!isNil(errorStatus)) {
      return {
        type: ChartViewType.KobolError,
        analysisId: id,
        errorStatus,
        failureMsg,
      } satisfies IChartView;
    } else {
      return {
        type: ChartViewType.KobolError,
        analysisId: id,
        errorStatus: AnalysisErrorStatus.Unknown,
        failureMsg,
      } satisfies IChartView;
    }
  } else {
    throw new UnreachableCaseError(status);
  }
};

const getBreakdownOverall = (analysis: IAnalysisResponse) => {
  const breakdown =
    'breakdown' in analysis.query ? analysis.query.breakdown : undefined;
  const subjectBucketDef =
    'subjectBucketDef' in analysis.query
      ? analysis.query.subjectBucketDef
      : undefined;
  const comparisonConfig =
    'comparisonConfig' in analysis ? analysis.comparisonConfig : undefined;

  if (breakdown) {
    return breakdown.overall
      ? IBreakdownOverallStatus.BreakdownOverall
      : IBreakdownOverallStatus.BreakdownNotOverall;
  } else if (!isEmpty(subjectBucketDef)) {
    if (!isNil(comparisonConfig)) {
      return IBreakdownOverallStatus.BreakdownOverallCompare;
    } else {
      return IBreakdownOverallStatus.BreakdownOverall;
    }
  } else if (!isNil(comparisonConfig)) {
    return IBreakdownOverallStatus.NoBreakdownCompare;
  } else {
    return IBreakdownOverallStatus.NoBreakdown;
  }
};

export const getComparisonInfo = (analysis: IAnalysisResponse) => {
  return (analysis.__typename === 'Expression' ||
    analysis.__typename === 'DataTable') &&
    !isNil(analysis.comparisonConfig) &&
    !isNil(analysis.comparisonData)
    ? {
        isEmpty: analysis.comparisonData.isEmpty,
        comparisonConfig: analysis.comparisonConfig,
      }
    : null;
};

export const getAnalysisMetadata = (
  analysis: IAnalysisResponse,
): IAnalysisMetadata => {
  const {
    queryType,
    id,
    display,
    description,
    container,
    start,
    end,
    status,
    timeUnit,
    isFavorite,
    exportUrl,
    referenceSql,
    creator,
    query,
    richTextSummary,
    datePicker,
    executedAt,
  } = analysis;
  const chartView = getChartView(analysis);

  const reportLink = getQueryTypePagePath(queryType, parseAnalysisId(id));

  const isBreakdownOverall = getBreakdownOverall(analysis);
  const comparisonInfo = getComparisonInfo(analysis);
  return {
    // basic infos
    analysisId: id,
    display,
    description,
    startDate: start,
    endDate: end,
    queryType,
    query,
    status,
    timeUnit,
    isFavorite,
    container,
    exportUrl,
    referenceSql,
    datePicker,
    // generated infos
    isBreakdownOverall,
    geo: analysis.__typename === 'Expression' ? analysis.geo : null,
    creator,
    executedAt,
    chartView,
    comparisonInfo,
    richTextSummary,
    reportLink,
  };
};
